import { addAddressPadding } from 'starknet';
import { getConfigManager } from '../config';
import { Decimal } from '../datastructures';

/**
 * Build a map from configured token ticker -> price.
 * Used as a symbol-based fallback for tokens not directly in rate maps
 * (e.g., native USDC -> USDC.e price).
 *
 * @param poolTokenRates  - address -> price from pool oracle
 * @param assetTokenRates - address -> price from lend/borrow oracle
 */
export function buildConfigTickerToPriceMap(
  poolTokenRates: Record<string, Decimal>,
  assetTokenRates: Record<string, Decimal>,
): Record<string, Decimal> {
  const map: Record<string, Decimal> = {};
  const config = getConfigManager().getConfig();

  for (const asset of Object.values(config.assets)) {
    const addr = addAddressPadding(asset.address);
    const price = poolTokenRates[addr] ?? assetTokenRates[addr];
    if (price) map[asset.ticker] = price;
  }

  for (const pool of Object.values(config.pools)) {
    const addrA = addAddressPadding(pool.pair.tokenA.address);
    const addrB = addAddressPadding(pool.pair.tokenB.address);
    const priceA = poolTokenRates[addrA] ?? assetTokenRates[addrA];
    const priceB = poolTokenRates[addrB] ?? assetTokenRates[addrB];
    if (priceA) map[pool.pair.tokenA.ticker] = priceA;
    if (priceB) map[pool.pair.tokenB.ticker] = priceB;
  }

  return map;
}

/**
 * Resolve the price for a token given its address and optional symbol.
 *
 * Lookup cascade:
 *   1. poolTokenRates by address (Pragma oracle prices for pool tokens)
 *   2. assetTokenRates by address (lend/borrow asset prices)
 *   3. Config-based fallback by symbol (e.g., "USDC" resolves via "USDC.e" price)
 */
export function resolveTokenPrice(
  address: string,
  symbol: string | undefined,
  poolTokenRates: Record<string, Decimal>,
  assetTokenRates: Record<string, Decimal>,
  configTickerToPrice: Record<string, Decimal>,
): Decimal | undefined {
  // 1. Direct lookup by address
  const directPrice = poolTokenRates[address] ?? assetTokenRates[address];
  if (directPrice) return directPrice;

  // 2. Fallback: match symbol to a configured token's price
  //    Handles cases like native "USDC" -> configured "USDC.e"
  if (symbol) {
    return configTickerToPrice[symbol] ?? configTickerToPrice[`${symbol}.e`];
  }

  return undefined;
}

/**
 * Build a reverse map from padded token address -> symbol using AVNU data.
 */
export function buildAvnuAddressToSymbolMap(
  avnuTokens: Record<string, { address: string; symbol: string }>,
): Record<string, string> {
  const map: Record<string, string> = {};
  for (const token of Object.values(avnuTokens)) {
    map[addAddressPadding(token.address)] = token.symbol;
  }
  return map;
}
